import { OrderLineType } from './OrderLine2';
export function getLegacyTotalPriceWithDiscount(orderLines, productCatalog, { includeReturnableItems = false, perLine = false, vouchers = [] } = {}, getTotalPriceWithoutDiscount = getLegacyTotalPriceWithoutDiscount) {
    const orderLinesWithPrices = [];
    let total = 0;
    const isSingleSelectionProduct = (orderLine) => {
        return orderLine.options && orderLine.options.canHaveMultipleProductSelections;
    };
    const hasIntersection = (arr1, arr2) => {
        return arr1.filter((x) => arr2.includes(x)).length > 0;
    };
    const isEmptyArrayOrNull = (testArray) => {
        if (Array.isArray(testArray) && testArray.length > 0) {
            return false;
        }
        return true;
    };
    const applicableDiscount = (orderLine) => {
        if (!('discount' in orderLine)) {
            return undefined;
        }
        if (!orderLine.discount) {
            return undefined;
        }
        const discount = Object.assign(Object.assign({}, orderLine.discount), { productId: orderLine.rootProductId });
        const discountApplicableVouchers = (() => {
            if (isEmptyArrayOrNull(discount.voucherPattern)) {
                return true;
            }
            else if (!isEmptyArrayOrNull(discount.voucherPattern) &&
                !isEmptyArrayOrNull(vouchers) &&
                hasIntersection(discount.voucherPattern, vouchers)) {
                return true;
            }
            return false;
        })();
        const discountApplicableGames = (() => {
            if (!discount.options) {
                return false;
            }
            if ([undefined, null].includes(discount.options.giftDiscount)) {
                return true;
            }
            if (discount.options.giftDiscount === true && productCatalog) {
                const product = productCatalog.getProduct(discount.productId);
                if (product && product.options.isPrize.fromGame && orderLine.addedByGame) {
                    return true;
                }
            }
            return false;
        })();
        if (discountApplicableVouchers && discountApplicableGames) {
            return discount;
        }
        return undefined;
    };
    const lineBasePrice = (orderLine) => {
        if (orderLine.type === OrderLineType.Product) {
            const quantity = orderLine.quantity ? orderLine.quantity : 1;
            return 'totalPrice' in orderLine && orderLine.totalPrice
                ? orderLine.totalPrice
                : orderLine.price * quantity;
        }
        else if (orderLine.type === OrderLineType.Aggregation) {
            const getSimpleBasePrice = (orderLine) => {
                const foundLine = orderLine.subLines.find((sbLine) => {
                    return sbLine.type === OrderLineType.Product && sbLine.rootProductId === sbLine.productId;
                });
                if (foundLine && 'quantity' in foundLine && 'price' in foundLine) {
                    const quantity = foundLine ? (foundLine.quantity ? foundLine.quantity : 1) : 1;
                    return foundLine.price * quantity;
                }
                else {
                    return 0;
                }
            };
            if (productCatalog) {
                const productId = orderLine.productId ? orderLine.productId : orderLine.rootProductId;
                const productFromCatalog = productCatalog.getProduct(productId, true);
                const quantity = orderLine.quantity ? orderLine.quantity : 1;
                if (productFromCatalog) {
                    return getProductPrice(productFromCatalog, productCatalog).basePrice * quantity;
                }
                return getSimpleBasePrice(orderLine);
            }
            else {
                return getSimpleBasePrice(orderLine);
            }
        }
    };
    let globalDiscountSum = 0;
    for (const i in orderLines) {
        const orderLine = orderLines[i];
        orderLinesWithPrices.push({
            productId: 'rootProductId' in orderLine ? orderLine.rootProductId : undefined,
            options: orderLine.options,
            totalPrice: getTotalPriceWithoutDiscount([orderLine], includeReturnableItems),
            discount: applicableDiscount(orderLine),
            basePrice: lineBasePrice(orderLine),
            type: orderLine.type,
            subLines: orderLine.subLines,
            quantity: isSingleSelectionProduct(orderLine) ? 1 : 'quantity' in orderLine ? orderLine.quantity : undefined,
        });
        const calculateGlobalDiscount = (line) => {
            return line.type === OrderLineType.Discount && line.price ? line.price : 0;
        };
        globalDiscountSum += calculateGlobalDiscount(orderLine);
    }
    const calculateSimpleDiscountFixedAmount = (line) => {
        if (!isNaN(line.discount.discountValue) && typeof line.discount.discountValue === 'number') {
            const discountedPrice = line.totalPrice * 100 - line.quantity * line.discount.discountValue * 100;
            return discountedPrice >= 0 ? discountedPrice / 100 : line.totalPrice;
        }
        return line.totalPrice;
    };
    const isNumber = (number) => {
        return typeof number === 'number' && !isNaN(number);
    };
    const singleLineProductQuantity = {};
    const priceLines = [];
    for (const i in orderLinesWithPrices) {
        const orderLine = orderLines[i];
        if (isSingleSelectionProduct(orderLine)) {
            if (typeof singleLineProductQuantity[orderLine.rootProductId] === 'number') {
                singleLineProductQuantity[orderLine.rootProductId] += 1;
            }
            else {
                singleLineProductQuantity[orderLine.rootProductId] = 1;
            }
        }
        const line = orderLinesWithPrices[i];
        if (line.discount) {
            const discountType = line.discount.discountType;
            if (discountType === 'simpleDiscountFixedAmount') {
                line.totalPrice = calculateSimpleDiscountFixedAmount(line);
            }
            else if (discountType === 'bulkDiscount' && !isSingleSelectionProduct(line)) {
                const minQuantityToApply = parseFloat(line.discount.options.minQuantityToApply);
                const newUnitPrice = parseFloat(line.discount.options.newUnitPrice);
                if (isNumber(minQuantityToApply) && isNumber(newUnitPrice) && line.quantity >= minQuantityToApply) {
                    line.totalPrice = newUnitPrice * line.quantity;
                }
            }
            else if (discountType === 'percentDiscount') {
                if (isNumber(line.discount.discountValue)) {
                    if (line.discount.options && line.discount.options.discountBasePriceOnly) {
                        line.totalPrice =
                            (line.totalPrice * 100 - line.basePrice * Math.min(100, line.discount.discountValue)) / 100;
                    }
                    else {
                        line.totalPrice =
                            (line.totalPrice * 100 - line.totalPrice * Math.min(100, line.discount.discountValue)) / 100;
                    }
                }
            }
            else if (discountType === 'twoForOne' && !isSingleSelectionProduct(line)) {
                line.totalPrice = (line.totalPrice * 100) / 2 / 100;
            }
            else if (discountType === 'twoForOne' && isSingleSelectionProduct(line)) {
                if (2 === singleLineProductQuantity[orderLine.rootProductId]) {
                    const discountValue = isNumber(line.discount.discountValue)
                        ? line.discount.discountValue
                        : lineBasePrice(line);
                    const discountedTotal = line.totalPrice * 100 - discountValue * 100;
                    line.totalPrice = isNumber(discountedTotal) && discountedTotal >= 0 ? discountedTotal / 100 : line.totalPrice;
                    singleLineProductQuantity[orderLine.rootProductId] = 0;
                }
            }
        }
        total += line.totalPrice;
        priceLines.push(line.totalPrice);
    }
    if (perLine) {
        return priceLines;
    }
    return total + globalDiscountSum;
}
export function getLegacyTotalPriceWithoutDiscount(orderLines, includeReturnableItems = false, getTotalPriceWithoutDiscount = getLegacyTotalPriceWithoutDiscount) {
    let total = 0;
    for (const i in orderLines) {
        if (orderLines[i].includedInMainPrice !== true && orderLines[i].type !== OrderLineType.Discount) {
            if (orderLines[i].type === OrderLineType.ReturnableItem) {
                if (includeReturnableItems) {
                    total += Math.round((orderLines[i].price || 0) * 100) * (orderLines[i].quantity || 0);
                }
            }
            else {
                total += Math.round((orderLines[i].price || 0) * 100) * (orderLines[i].quantity || 0);
                total += Math.round(getTotalPriceWithoutDiscount(orderLines[i].subLines, true) * 100);
            }
        }
    }
    for (const i in orderLines) {
        if (orderLines[i].includedInMainPrice !== true) {
            if (orderLines[i].type === OrderLineType.Config && orderLines[i].selectedOption) {
                if (Array.isArray(orderLines[i].selectedOption)) {
                    orderLines[i].selectedOption.forEach((opt) => {
                        if (opt.priceModifier) {
                            total += Math.round((opt.priceModifier.value || 0) * 100);
                        }
                    });
                }
                else if (orderLines[i].selectedOption.priceModifier) {
                    total += Math.round((orderLines[i].selectedOption.priceModifier.value || 0) * 100);
                }
            }
        }
    }
    return total / 100;
}
export const getProductPrice = (product, productCatalog, menuSize = -1) => {
    const discountValue = product.discount && product.discount.discountType === 'simpleDiscountFixedAmount'
        ? product.discount.discountValue
        : 0;
    if (product.price) {
        return {
            basePrice: product.price,
            discountPrice: product.price - discountValue,
        };
    }
    else {
        const calculatedPrice = product.groups.reduce((accumulator, currentValue) => {
            const groupProduct = currentValue.options.defaultSelected
                ? productCatalog.getProduct(currentValue.options.defaultSelected, true)
                : null;
            if (currentValue.options.showIfConfigurationOptionSelected) {
                const config = product.configurations.find((c) => c.id === currentValue.options.showIfConfigurationOptionSelected.configurationId);
                const index = menuSize !== -1 ? menuSize : config.options.defaultSelected;
                if (index !== currentValue.options.showIfConfigurationOptionSelected.optionIndex) {
                    return accumulator;
                }
            }
            return accumulator + (groupProduct ? groupProduct.getPriceInGroup() : 0);
        }, 0);
        return {
            basePrice: calculatedPrice,
            discountPrice: calculatedPrice - discountValue,
        };
    }
};
